import React from "react";
import { Helmet } from "react-helmet";
import OsChrome from "./OsChrome.jsx";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "react-query";
import { fetchGetProducts } from "../../../queries/products";
import { getSiteInfo } from "../../../queries/citizen";
import SiteContext from "./SiteContext.jsx";
import PlatformContext from "./PlatformContext.jsx";
import networkSelectors from "../../../selectors/network";

export type SiteInfoAppletData = {
  description: null;
  iconUrl: string;
  targetUrl: string;
  title: string;
};

export type SiteInfoData = {
  accentColor: string;
  applets: SiteInfoAppletData[];
  backgroundImageUrl: string;
  defaultLocation: { latitude: number; longitude: number };
  geocodeMethod: string;
  logoImageUrl: string;
  name: string;
  primaryColor: string;
  welcomeMessage: string;
};

export type ProductsData = {
  meta: any;
  payload: {
    data: {
      attributes: {
        abbreviation: any;
        category: string;
        description: any;
        entitlements: string[];
        name: string;
        sellable: boolean;
      };
      id: string;
      type: string;
    }[];
  };
  type: string;
};

const allowedPlatforms = ["android", "ios"] as const;
type Platform = (typeof allowedPlatforms)[number];

export default function Body({ children }: React.PropsWithChildren<{}>) {
  const { site } = useParams<{ site: string }>();
  const location = useLocation();
  const dispatch = useDispatch();
  const productsQuery = useQuery<ProductsData>(["products", site], fetchGetProducts(dispatch));
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const siteInfoQuery = useQuery<SiteInfoData>(["citizen.site", site], getSiteInfo(networkHeaders));

  const platformParam = new URLSearchParams(location.search).get("platform");

  const platform = (allowedPlatforms.includes(platformParam as Platform) ? platformParam : "android") as Platform;

  const { backgroundImageUrl = "", applets = [], primaryColor } = siteInfoQuery.data || {};
  const products = productsQuery.data?.payload?.data || [];
  const hasCrm = products.some((product) => product?.attributes?.name === "CRM");
  const hasNotify = products.some((product) => product?.attributes?.name === "Communications");

  const previewFontClass = platform === "ios" ? "tw-font-previewIos tw-tracking-wide" : "tw-font-previewAndroid";

  return (
    <PlatformContext.Provider
      value={{
        platform,
        chromeHeight: 25,
      }}
    >
      <SiteContext.Provider
        value={{
          primaryColor: primaryColor || "#000",
          backgroundImageUrl,
          applets,
          hasCrm,
          hasNotify,
        }}
      >
        <Helmet>
          <meta name='viewport' content='width=device-width' />
        </Helmet>

        <div
          className={`${previewFontClass} tw-flex-col tw-leading-tight tw-text-[#202020]`}
          style={{ "--primary-color": primaryColor } as React.CSSProperties}
        >
          <OsChrome />
          {children}
        </div>
      </SiteContext.Provider>
    </PlatformContext.Provider>
  );
}
